import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import OlbIntegrationCard from './OlbIntegrationCard/OlbIntegrationCard';
import TrackingForm from './TrackingForm/TrackingForm';
import styles from './Integrations.module.scss';

type ActiveView = 'list' | 'ga' | 'gtm';

const GA_ICON = '/images/settings/integrations/google-analytics.svg';
const GTM_ICON = '/images/settings/integrations/google-tag-manager.svg';

const Integrations: FunctionComponent = () => {
    const [activeView, setActiveView] = useState<ActiveView>('list');

    const gaTrackingId = useSelector(
        (state: RootState) => state.marketplace.settingsMarketplace.integrations.googleAnalyticsTrackingId
    );
    const gtmTrackingId = useSelector(
        (state: RootState) => state.marketplace.settingsMarketplace.integrations.gtmTrackingId
    );

    if (activeView === 'ga') {
        return (
            <TrackingForm
                icon={GA_ICON}
                title="Google Analytics"
                fieldLabel="GA4 Measurement Id"
                placeholder="G-DY0RZ4W3U5"
                infoText={
                    <>
                        To ensure accurate tracking, please <strong>enable cross-domain</strong> measurement in your
                        Google Analytics settings. Follow the setup instructions{' '}
                        <a
                            href="https://support.google.com/analytics/answer/10071811#setup"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </>
                }
                currentValue={gaTrackingId ?? ''}
                integration="ga"
                onBack={() => setActiveView('list')}
            />
        );
    }

    if (activeView === 'gtm') {
        return (
            <TrackingForm
                icon={GTM_ICON}
                title="Google Tag Manager"
                fieldLabel="GTM Container Id"
                placeholder="GTM-XXXXXXX"
                infoText={
                    <>
                        Add your GTM container to manage and deploy marketing tags on your booking page. Follow the
                        setup instructions{' '}
                        <a
                            href="https://developers.google.com/tag-platform/devguides/cross-domain#tag-manager"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </>
                }
                currentValue={gtmTrackingId ?? ''}
                integration="gtm"
                onBack={() => setActiveView('list')}
            />
        );
    }

    return (
        <div className={styles.container}>
            <OlbIntegrationCard
                icon={GA_ICON}
                title="Google Analytics"
                description="Connect your Google Analytics account to track your online booking traffic."
                isConnected={!!gaTrackingId}
                onClick={() => setActiveView('ga')}
            />
            <OlbIntegrationCard
                icon={GTM_ICON}
                title="Google Tag Manager"
                description="Connect your Google Tag Manager account to manage tracking tags for your online booking."
                isConnected={!!gtmTrackingId}
                onClick={() => setActiveView('gtm')}
            />
        </div>
    );
};

export default Integrations;
